import React from 'react';

import cx from 'classnames';

import InfoIcon from 'components/Icons/Info.svg';

import { useServiceSelection } from 'hooks/page/services/useServiceSelection';
import { TServiceProps } from 'hooks/page/services/useServicesPage';
import { useGGTheme } from 'hooks/theme/useGGTheme';

import { BuyNowPayLaterPromo } from 'routes/invoices/components/BuyNowPayLaterPromo/BuyNowPayLaterPromo';
import { MAX_SERVICE_DESCRIPTION } from 'routes/services/constants';

import s from './ClassicService.module.css';

type NoMatchingProvidersMessageProps = {
  className?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
};

const NoMatchingProvidersMessage = ({
  className,
  onClick,
  ...otherProps
}: NoMatchingProvidersMessageProps) => {
  return (
    <div className={cx(s.noMatchingProvidersMsg, className)} {...otherProps}>
      <button className={s.disabledText} onClick={onClick}>
        <span>No matching service providers</span>
        <i className={s.disabledServiceIcon}>
          <InfoIcon />
        </i>
      </button>
    </div>
  );
};

const ClassicService = ({
  service,
  listView = false,
  disabled = false,
  selected = false,
  serviceProvider,
  selectedOption,
  onClick,
}: TServiceProps) => {
  const {
    bnplUnitPrice,
    displayedPrice,
    toggleMobileDescription,
    showMobileDescription,
    toggleNoMatchingProvidersModal,
    onServiceClick,
    depositText,
    durationText,
    optionText,
    renderNoMatchingProviderModal,
    bnplCapabilities,
  } = useServiceSelection({
    service,
    selectedOption,
    disabled,
    onClick,
  });

  const { name, description, image } = service;

  const { theme } = useGGTheme();
  const promoTextColor = theme?.palette.textSecondary as string;
  const promoBackgroundColor = '#fff';

  const summarySmallFont =
    description && description.length > MAX_SERVICE_DESCRIPTION
      ? s.summarySmall
      : {};
  const descriptionClass =
    description && description.length > 200
      ? s.mobileDescriptionInner
      : undefined;

  const selectedText = selected ? 'Unselect' : 'Select';

  let popovertarget;
  let popovertargetaction;
  const hasOptions = Boolean(service.options?.length);

  if (hasOptions && !selected) {
    popovertarget = 'service-option-modal';
  }
  if (hasOptions) {
    popovertargetaction = 'show';
  }

  return (
    <>
      {renderNoMatchingProviderModal()}
      <article
        className={cx(s.root, {
          [s.disabled]: disabled,
          [s.listItem]: listView,
          [s.selected]: selected,
        })}
      >
        {disabled && (
          <NoMatchingProvidersMessage
            onClick={toggleNoMatchingProvidersModal}
          />
        )}
        <div className={s.header} style={{ backgroundImage: `url(${image})` }}>
          {description && !disabled && (
            <button
              className={s.mobileMoreInfoBtn}
              onClick={toggleMobileDescription}
              aria-label="Toggle description"
            >
              <InfoIcon />
            </button>
          )}
          {disabled && (
            <NoMatchingProvidersMessage
              className={s.smallView}
              onClick={toggleNoMatchingProvidersModal}
            />
          )}
          <img className={s.image} src={image} alt={name} />
          <div
            className={cx(s.mobileDescriptionWrap, {
              [s.showMobileDescription]: showMobileDescription,
            })}
          >
            <div className={descriptionClass}>{description}</div>
          </div>
        </div>

        <div className={s.main}>
          {disabled && <div className={s.disabledOverlay} />}
          <div className={s.description}>
            <h2
              className={cx(s.title, {
                [s.providerSeparator]: selected && serviceProvider,
              })}
            >
              {name}
            </h2>
            {selected && serviceProvider && (
              <p className={s.provider}>WITH {serviceProvider}</p>
            )}
            {depositText && <p className={s.deposit}>{depositText}</p>}
            <p className={cx(s.summary, summarySmallFont)}>{description}</p>
          </div>
          <div className={s.detailWrapper}>
            <div className={s.details}>
              {optionText ? (
                <div data-testid="option" className={s.serviceOption}>
                  {optionText}
                </div>
              ) : null}
              <span className={s.options}>
                <span className={s.option}>
                  <b>Price:</b> {displayedPrice}
                </span>
                <span className={s.option}>
                  <b>Duration:</b> {durationText}
                </span>
              </span>
            </div>
            {bnplUnitPrice ? (
              <div className={s.bnplEligible}>
                <BuyNowPayLaterPromo
                  unitAmount={bnplUnitPrice}
                  textAlign="right"
                  paymentMethodTypes={bnplCapabilities}
                  textColor={promoTextColor}
                  backgroundColor={promoBackgroundColor}
                />
              </div>
            ) : null}
          </div>
          <button
            onClick={onServiceClick}
            disabled={disabled}
            className={cx(s.button, {
              [s.buttonSelected]: selected,
            })}
            title={`${selectedText} ${name}`}
            type="button"
            // eslint-disable-next-line react/no-unknown-property
            popovertarget={popovertarget}
            // eslint-disable-next-line react/no-unknown-property
            popovertargetaction={popovertargetaction}
          >
            {selectedText}
          </button>
        </div>
      </article>
    </>
  );
};

export { ClassicService };
